import { Badge } from "../ui/badge";
import { DollarSign, Users, GraduationCap, Sparkles, Crown } from "lucide-react";

const PLAN_STYLES = {
  teacher_pro: { label: "Pro", cls: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30", Icon: Crown },
  teacher_plus: { label: "Plus", cls: "bg-sky-500/20 text-sky-700 dark:text-sky-400 border-sky-500/30", Icon: Sparkles },
};

export const TeacherPlanBadge = ({ tier, userId }) => {
  const style = PLAN_STYLES[tier];
  if (!style) {
    return (
      <Badge variant="outline" className="text-muted-foreground" data-testid={`teacher-plan-${userId}`}>Free</Badge>
    );
  }
  const { label, cls, Icon } = style;
  return (
    <Badge className={cls} data-testid={`teacher-plan-${userId}`}>
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

const money = (n) => `A$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Tile = ({ label, value, sub, testId }) => (
  <div className="rounded-xl border border-border/40 bg-muted/30 px-4 py-3" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="font-heading font-semibold text-lg leading-tight">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

export const SubscriptionSummary = ({ summary }) => {
  if (!summary) return null;
  const t = summary.tier_counts || {};
  const paidLines = (summary.breakdown || []).filter((b) => b.count > 0);

  return (
    <div className="mb-6 grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,2fr)]" data-testid="subscription-summary">
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex gap-4" data-testid="revenue-card">
        <div className="p-2.5 rounded-xl bg-emerald-500/15 h-fit">
          <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Est. monthly revenue</p>
          <p className="font-heading font-bold text-2xl sm:text-3xl text-emerald-700 dark:text-emerald-400" data-testid="revenue-total">
            {money(summary.monthly_total)}
          </p>
          <p className="text-xs text-muted-foreground mt-1" data-testid="revenue-paying-count">
            {summary.paying_count} paying {summary.paying_count === 1 ? "subscriber" : "subscribers"}
            {summary.cancelling_count > 0 && <span className="text-amber-600 dark:text-amber-400"> · {summary.cancelling_count} cancelling at period end</span>}
          </p>
          {paidLines.length > 0 && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
              {paidLines.map((b) => (
                <span key={b.tier} data-testid={`revenue-line-${b.tier}`}>
                  {b.count} × {b.label} @ {money(b.price)} = <span className="font-medium text-foreground">{money(b.total)}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-border/40 p-4">
        <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
          <GraduationCap className="w-4 h-4" /> Teachers
          <span className="mx-1 text-border">|</span>
          <Users className="w-4 h-4" /> Learners
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Tile label="Teacher Free" value={t.teacher_free || 0} sub="Starter licence" testId="tier-count-teacher-free" />
          <Tile label="Teacher Plus" value={t.teacher_plus || 0} sub="A$14 / mo" testId="tier-count-teacher-plus" />
          <Tile label="Teacher Pro" value={t.teacher_pro || 0} sub="A$29 / mo" testId="tier-count-teacher-pro" />
          <Tile label="Student Plan" value={t.student || 0} sub="A$8 / mo" testId="tier-count-student" />
          <Tile label="School Students" value={t.school_student || 0} sub="Free via school" testId="tier-count-school-student" />
          <Tile label="Free" value={t.free || 0} testId="tier-count-free" />
        </div>
      </div>
    </div>
  );
};
